import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  styled
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import * as shiftDataService from '../../services/migration/shiftDataService';
import { useTranslation } from 'react-i18next';

// Personel tipi
interface Personnel {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  branchesId?: string | null;
}

// Vardiya kartı için styled component
const VardiyaCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
}));

interface VardiyaListesiProps {
  branchId?: string;
  isManager?: boolean;
}

const VardiyaListesi: React.FC<VardiyaListesiProps> = ({ branchId, isManager = false }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  // ✅ AuthContext'ten personnelList ve getPersonnelInfo helper'ını al
  const { currentUser, userDetails, getPersonnelInfo, personnelList: authPersonnelList } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [vardiyaAdi, setVardiyaAdi] = useState('');
  const [girisZamani, setGirisZamani] = useState('');
  const [gecKalmaToleransi, setGecKalmaToleransi] = useState(10);
  const [cikisZamani, setCikisZamani] = useState('');
  const [erkenCikmaToleransi, setErkenCikmaToleransi] = useState(10);
  const [cikisErtesiGun, setCikisErtesiGun] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState<'single' | 'dual'>('single');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vardiyalar, setVardiyalar] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Personel seçme modal state'leri
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [selectedVardiyaId, setSelectedVardiyaId] = useState<string | number | null>(null);
  const [selectedVardiyaBranchId, setSelectedVardiyaBranchId] = useState<string | null>(null);
  const [personnelList, setPersonnelList] = useState<Personnel[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [filteredPersonnel, setFilteredPersonnel] = useState<Personnel[]>([]);

  // Vardiya silme için state'ler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteVardiyaId, setDeleteVardiyaId] = useState<string | number | null>(null);
  const [deleteVardiyaName, setDeleteVardiyaName] = useState('');
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);

  // Şube seçimi için state
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Vardiyaları veritabanından getir
  useEffect(() => {
    const fetchVardiyalar = async () => {
      if (!userDetails?.companyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Önce şubeleri yükle
        await fetchBranches();
        
        // SQL-once (gts_hr_api) + HybridFallback ile vardiyaları çek
        const shifts = await shiftDataService.getShifts(userDetails.companyId);

        if (shifts && shifts.length > 0) {
          // Her vardiyanın atanan personel listesini doldur (SQL modunda assignedCount geldiği için
          // personel listesi ayrı uçtan; fallback'te personnelUids inline gelir).
          let vardiyalar = await Promise.all(shifts.map(async (s) => {
            let personnel = s.personnelUids;
            if (!personnel) {
              try {
                personnel = (s.assignedCount ?? 0) > 0 ? await shiftDataService.getShiftPersonnel(s.id) : [];
              } catch {
                personnel = [];
              }
            }
            return {
              id: s.id,
              name: s.name || tr('İsimsiz Vardiya', 'Unnamed Shift'),
              startTime: s.startTime || '',
              endTime: s.endTime || '',
              lateTolerance: s.lateTolerance || 0,
              earlyExitTolerance: s.earlyExitTolerance || 0,
              isNextDayExit: s.isNextDayExit || false,
              personnel,
              branchesId: s.branchesId || null,
              attendanceMode: s.attendanceMode === 'dual' ? 'dual' : 'single',
            };
          }));

          // Şube yöneticisi için sadece kendi şubesine ait vardiyaları listele
          if (isManager && branchId) {
            vardiyalar = vardiyalar.filter(vardiya => vardiya.branchesId === branchId);
          }

          setVardiyalar(vardiyalar);
        } else {
          setVardiyalar([]);
        }
      } catch (error) {
        console.error('Vardiyalar yüklenirken hata:', error);
        setOpenSnackbar(true);
        setSnackbarMessage(tr('Vardiyalar yüklenirken bir hata oluştu', 'An error occurred while loading shifts'));
        setSnackbarSeverity('error');
      } finally {
        setLoading(false);
      }
    };

    fetchVardiyalar();
  }, [userDetails, isManager, branchId]);

  // Personel listesini getir
  // ✅ PERFORMANS: AuthContext'teki personnelList'i kullanarak N+1 Cloud Function sorununu çöz
  // Eski kod: her personel için ayrı getUserData çağrısı yapıyordu (40+ Cloud Function çağrısı)
  // Yeni kod: AuthContext cache'inden tek seferde alıyor (0 ek Cloud Function çağrısı)
  const fetchPersonnel = async () => {
    if (!userDetails?.companyId) return;
    
    setLoadingPersonnel(true);
    try {
      // Tüm atanmış personelleri topla (mevcut vardiya hariç) — yüklü `vardiyalar` state'inden.
      const assignedPersonnel = new Set<string>();
      vardiyalar.forEach((shift) => {
        if (shift.id !== selectedVardiyaId && Array.isArray(shift.personnel)) {
          shift.personnel.forEach((personId: string) => assignedPersonnel.add(personId));
        }
      });
      
      // ✅ AuthContext'teki personnelList'i kullan - SIFIR ek API çağrısı!
      // personnelList zaten branchesId bilgisini içeriyor
      if (authPersonnelList.length > 0) {
        
        // Silinmiş ve başka vardiyaya atanmış personelleri filtrele
        let resultList = authPersonnelList
          .filter(person => !assignedPersonnel.has(person.id))
          .map(person => ({
            id: person.id,
            name: person.name || tr('İsimsiz Personel', 'Unnamed Personnel'),
            email: person.email || '',
            phone: person.phone || '',
            branchesId: person.branchesId || null
          }));
        
        // Şube filtresi: Vardiya bir şubeye aitse sadece o şubenin personellerini göster
        const effectiveBranchFilter = selectedVardiyaBranchId || (isManager && branchId ? branchId : null);
        
        if (effectiveBranchFilter) {
          console.log(`Şube ID'si (${effectiveBranchFilter}) için personel filtreleniyor`);
          resultList = resultList.filter(person => person.branchesId === effectiveBranchFilter);
          console.log(`Şubeye göre filtrelenen personel sayısı: ${resultList.length}`);
        } else {
          console.log("Vardiya şubeye ait değil, tüm personel listeleniyor");
        }
        
        setPersonnelList(resultList);
        setFilteredPersonnel(resultList);
      } else {
        // AuthContext henüz yüklenmediyse, fallback olarak getPersonnel çağır
        console.log('⚠️ AuthContext personnelList boş, Cloud Function ile yükleniyor...');
        const personnelData = await firebaseDataService.getPersonnel(userDetails.companyId);
        
        let data: Record<string, any> = {};
        if (Array.isArray(personnelData)) {
          personnelData.forEach((p: any) => { data[p.id] = p; });
        } else if (personnelData) {
          data = personnelData as Record<string, any>;
        }
        
        if (Object.keys(data).length > 0) {
          let resultList = Object.entries(data)
            .filter(([id, personData]: [string, any]) => {
              if (personData.isDeleted) return false;
              return !assignedPersonnel.has(id);
            })
            .map(([id, personData]: [string, any]) => ({
              id,
              name: personData.name || tr('İsimsiz Personel', 'Unnamed Personnel'),
              email: personData.email || '',
              phone: personData.phone || '',
              branchesId: personData.branchesId || null
            }));
          
          // Şube filtresi
          const effectiveBranchFilter = selectedVardiyaBranchId || (isManager && branchId ? branchId : null);
          
          if (effectiveBranchFilter) {
            console.log(`Şube ID'si (${effectiveBranchFilter}) için personel filtreleniyor (fallback)`);
            resultList = resultList.filter(person => person.branchesId === effectiveBranchFilter);
            console.log(`Şubeye göre filtrelenen personel sayısı: ${resultList.length}`);
          }
          
          setPersonnelList(resultList);
          setFilteredPersonnel(resultList);
        } else {
          setPersonnelList([]);
          setFilteredPersonnel([]);
        }
      }
    } catch (error) {
      console.error("Personel listesi yüklenirken hata:", error);
    } finally {
      setLoadingPersonnel(false);
    }
  };

  // Personel modalı açıldığında personel listesini getir
  useEffect(() => {
    if (personnelModalOpen) {
      fetchPersonnel();
    }
  }, [personnelModalOpen]);

  // Personel arama filtrelemesi
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPersonnel(personnelList);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = personnelList.filter(person => 
      person.name.toLowerCase().includes(term) || 
      (person.email && person.email.toLowerCase().includes(term)) ||
      (person.phone && person.phone.includes(term))
    );
    
    setFilteredPersonnel(filtered);
  }, [searchTerm, personnelList]);

  // Personel seçme modalını açma işlevi
  const handleOpenPersonnelModal = (vardiyaId: string | number) => {
    setSelectedVardiyaId(vardiyaId);
    
    // Vardiya bilgilerini al
    const vardiya = vardiyalar.find(v => v.id === vardiyaId);
    if (vardiya) {
      // Vardiya şube ID'sini ayarla
      setSelectedVardiyaBranchId(vardiya.branchesId);
      console.log(`Vardiya şube ID'si: ${vardiya.branchesId}`);
      
      // Personel bilgilerini al
      if (vardiya.personnel) {
        console.log("Personnel verisi:", vardiya.personnel);
        
        // Personnel bir dizi veya obje olabilir, kontrol et
        if (Array.isArray(vardiya.personnel)) {
          setSelectedPersonnel(vardiya.personnel);
        } else if (typeof vardiya.personnel === 'object') {
          // Obje formatındaysa (key: true) anahtarları al
          setSelectedPersonnel(Object.keys(vardiya.personnel));
        } else {
          setSelectedPersonnel([]);
        }
      } else {
        setSelectedPersonnel([]);
      }
    } else {
      setSelectedVardiyaBranchId(null);
      setSelectedPersonnel([]);
    }
    
    setPersonnelModalOpen(true);
  };

  // Personel seçme modalını kapatma işlevi
  const handleClosePersonnelModal = () => {
    setPersonnelModalOpen(false);
    setSelectedVardiyaId(null);
    setSelectedVardiyaBranchId(null);
    setSelectedPersonnel([]);
    setSearchTerm('');
  };

  // Tüm personeli seçme/kaldırma işlevi
  const handleToggleAll = () => {
    if (selectedPersonnel.length === filteredPersonnel.length) {
      setSelectedPersonnel([]);
    } else {
      setSelectedPersonnel(filteredPersonnel.map(p => p.id));
    }
  };

  // Tek personel seçme/kaldırma işlevi
  const handleTogglePersonnel = (id: string) => {
    const selectedIndex = selectedPersonnel.indexOf(id);
    let newSelected = [...selectedPersonnel];
    
    if (selectedIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(selectedIndex, 1);
    }
    
    setSelectedPersonnel(newSelected);
  };

  // Personel kaydetme işlevi
  const handleSavePersonnel = async () => {
    if (!selectedVardiyaId || !userDetails?.companyId) return;
    
    setIsSubmitting(true);
    try {
      // Vardiyaya personel ata (SADECE SQL; replace + tek-vardiya kurali)
      await shiftDataService.assignShiftPersonnel(selectedVardiyaId, selectedPersonnel, true);
      
      // Başarı mesajı
      setSnackbarMessage(tr('Personel atama başarıyla kaydedildi', 'Personnel assignment saved successfully'));
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Vardiyalar listesini güncelle
      setVardiyalar(prevVardiyalar => 
        prevVardiyalar.map(vardiya => 
          vardiya.id === selectedVardiyaId
            ? { ...vardiya, personnel: selectedPersonnel }
            : vardiya
        )
      );
      
      // Modalı kapat
      handleClosePersonnelModal();
    } catch (error) {
      console.error('Personel kaydedilirken hata:', error);
      setSnackbarMessage(tr('Personel kaydedilirken bir hata oluştu', 'An error occurred while saving personnel'));
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Şubeleri veritabanından getir
  const fetchBranches = async () => {
    if (!userDetails?.companyId) return;
    
    setLoadingBranches(true);
    try {
      // Cloud Functions üzerinden şubeleri çek
      const branchesData = await firebaseDataService.getBranches(userDetails.companyId);
      
      if (branchesData && branchesData.length > 0) {
        const branchesArray = branchesData.map((branch: any) => ({
          id: branch.id,
          name: branch.name || tr('İsimsiz Şube', 'Unnamed Branch')
        }));
        
        setBranches(branchesArray);
        
        // Şube müdürü ise kendi şubesini otomatik seç
        if (isManager && branchId) {
          const managerBranch = branchesArray.find(branch => branch.id === branchId);
          if (managerBranch) {
            setSelectedBranch(managerBranch);
            console.log(`Şube müdürü için otomatik şube seçildi: ${managerBranch.name} (${managerBranch.id})`);
          } else {
            console.warn(`Şube müdürünün şubesi bulunamadı: ${branchId}`);
          }
        }
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    } finally {
      setLoadingBranches(false);
    }
  };

  // Modal açıldığında şubeleri getir - artık branches bağımlılığı yok
  useEffect(() => {
    if (open) {
      fetchBranches();
    }
  }, [open, isManager, branchId]); // branches bağımlılığını kaldırdık

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Formu sıfırla
    setVardiyaAdi('');
    setGirisZamani('');
    setGecKalmaToleransi(10);
    setCikisZamani('');
    setErkenCikmaToleransi(10);
    setCikisErtesiGun(false);
    setAttendanceMode('single');
    setSelectedBranch(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!vardiyaAdi) {
      setError(tr('Vardiya adı gereklidir', 'Shift name is required'));
      return;
    }
    if (!girisZamani) {
      setError(tr('Giriş zamanı gereklidir', 'Start time is required'));
      return;
    }
    if (!cikisZamani) {
      setError(tr('Çıkış zamanı gereklidir', 'End time is required'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      // Şirket ID'sini userDetails'dan al
      const companyId = userDetails?.companyId;
      
      if (!companyId) {
        throw new Error(tr('Şirket bilgisi bulunamadı', 'Company information not found'));
      }
      
      // Şube ID'sini belirle
      const branchesId = isManager && branchId ? branchId : (selectedBranch ? selectedBranch.id : null);

      // Yeni vardiyayı SQL'e kaydet (gts_hr_api)
      const shiftId = await shiftDataService.createShift({
        name: vardiyaAdi,
        startTime: girisZamani,
        endTime: cikisZamani,
        lateTolerance: gecKalmaToleransi,
        earlyExitTolerance: erkenCikmaToleransi,
        isNextDayExit: cikisErtesiGun,
        branchesId,
        attendanceMode,
      });
      
      // Listeye yeni vardiyayı ekle (veritabanından tekrar çekmek yerine)
      setVardiyalar(prevVardiyalar => [
        ...prevVardiyalar,
        {
          id: shiftId,
          name: vardiyaAdi,
          startTime: girisZamani,
          endTime: cikisZamani,
          lateTolerance: gecKalmaToleransi,
          earlyExitTolerance: erkenCikmaToleransi,
          isNextDayExit: cikisErtesiGun,
          personnel: [],
          branchesId,
          attendanceMode,
        }
      ]);
      
      // Başarı mesajı göster
      setSnackbarMessage(tr('Vardiya başarıyla eklendi', 'Shift added successfully'));
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Başarılı olursa modalı kapat
      handleClose();
    } catch (error) {
      console.error('Vardiya eklenirken hata oluştu:', error);
      setError(tr('Vardiya eklenirken bir hata oluştu. Lütfen tekrar deneyin.', 'An error occurred while adding the shift. Please try again.'));
      
      // Hata mesajı göster
      setSnackbarMessage(tr('Vardiya eklenirken bir hata oluştu', 'An error occurred while adding the shift'));
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Snackbar kapatma
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Vardiya silme fonksiyonları
  const handleDeleteClick = (vardiya: any) => {
    // Personel atanmış mı kontrol et
    const personnelCount = vardiya.personnel ? 
      (Array.isArray(vardiya.personnel) ? vardiya.personnel.length : Object.keys(vardiya.personnel).length) : 0;
    
    if (personnelCount > 0) {
      // Personel atanmışsa hata modalını göster
      setDeleteErrorOpen(true);
    } else {
      // Personel yoksa silme onay modalını göster
      setDeleteVardiyaId(vardiya.id);
      setDeleteVardiyaName(vardiya.name);
      setDeleteConfirmOpen(true);
    }
  };

  const handleCloseDeleteError = () => {
    setDeleteErrorOpen(false);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteVardiyaId(null);
    setDeleteVardiyaName('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteVardiyaId || !userDetails?.companyId) return;
    
    setIsSubmitting(true);
    try {
      // Vardiyayı SQL'den sil (soft-delete + atamalar)
      await shiftDataService.deleteShift(deleteVardiyaId);
      
      // Vardiyalar listesini güncelle
      setVardiyalar(prevVardiyalar => prevVardiyalar.filter(v => v.id !== deleteVardiyaId));
      
      // Başarı mesajı
      setSnackbarMessage(tr('Vardiya başarıyla silindi', 'Shift deleted successfully'));
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Modalı kapat
      handleCloseDeleteConfirm();
    } catch (error) {
      console.error('Vardiya silinirken hata:', error);
      setSnackbarMessage(tr('Vardiya silinirken bir hata oluştu', 'An error occurred while deleting the shift'));
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Excel'e veri aktarma olayını dinle
  useEffect(() => {
    const handleExportToExcel = (event: CustomEvent) => {
      const { worksheet } = event.detail;
      
      // Vardiya verilerini Excel'e ekle
      vardiyalar.forEach(vardiya => {
        const personnelCount = vardiya.personnel ? vardiya.personnel.length : 0;
        
        worksheet.addRow({
          name: vardiya.name,
          startTime: vardiya.startTime,
          endTime: vardiya.endTime,
          personnelCount: `${personnelCount} ${tr('kişi', 'people')}`,
          lateTolerance: `${vardiya.lateTolerance || 10} ${tr('dk', 'min')}`,
          earlyExitTolerance: `${vardiya.earlyExitTolerance || 10} ${tr('dk', 'min')}`
        });
      });
    };
    
    // Event listener'ı ekle
    window.addEventListener('export-vardiya-to-excel', handleExportToExcel as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('export-vardiya-to-excel', handleExportToExcel as EventListener);
    };
  }, [vardiyalar]);

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ mb: { xs: 0, sm: 1 } }}>
          {tr('Vardiya Listesi', 'Shift List')}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />} 
          onClick={handleClickOpen}
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          {tr('Vardiya Ekle', 'Add Shift')}
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {tr('Vardiyalar yükleniyor...', 'Loading shifts...')}
          </Typography>
        </Box>
      ) : vardiyalar.length === 0 ? (
        <Paper 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            height: '50vh',
            justifyContent: 'center'
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.7 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {tr('Henüz vardiya bulunmamaktadır', 'No shifts found yet')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: '500px' }}>
            {tr('Personelleriniz için vardiya ekleyerek giriş ve çıkış saatlerini takip edebilirsiniz.', 'You can track check-in and check-out times by adding shifts for your personnel.')}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={handleClickOpen}
          >
            {tr('Vardiya Ekle', 'Add Shift')}
          </Button>
        </Paper>
      ) : isMobile ? (
        /* Mobil Kart Görünümü */
        <Box sx={{ pb: 2 }}>
          {vardiyalar.map((vardiya) => {
            const personnelCount = vardiya.personnel ? 
              (Array.isArray(vardiya.personnel) ? vardiya.personnel.length : Object.keys(vardiya.personnel).length) : 0;
            const branch = branches.find(branch => branch.id === vardiya.branchesId);
            const branchName = branch ? branch.name : '';
            
            return (
              <VardiyaCard key={vardiya.id}>
                <CardContent sx={{ pb: 1 }}>
                  {/* Üst Kısım - Vardiya Adı ve Şube */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                        {vardiya.name}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {branchName && (
                          <Chip 
                            label={branchName} 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                        <Chip
                          icon={<QrCode2Icon sx={{ fontSize: 14 }} />}
                          label={vardiya.attendanceMode === 'dual' ? tr('Çift QR', 'Dual QR') : tr('Tek QR', 'Single QR')}
                          size="small"
                          variant={vardiya.attendanceMode === 'dual' ? 'filled' : 'outlined'}
                          color={vardiya.attendanceMode === 'dual' ? 'secondary' : 'default'}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Box>
                    <Chip
                      label={`${personnelCount} ${tr('kişi', 'people')}`}
                      size="small"
                      color={personnelCount > 0 ? "primary" : "default"}
                      variant={personnelCount > 0 ? "filled" : "outlined"}
                      icon={<PersonIcon sx={{ fontSize: 14 }} />}
                      sx={{ height: 26 }}
                    />
                  </Box>
                  
                  {/* Orta Kısım - Saat Bilgileri */}
                  <Grid container spacing={1.5} sx={{ mb: 1 }}>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {tr('Giriş Saati', 'Start Time')}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          {vardiya.startTime}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          +{vardiya.lateTolerance || 10} {tr('dk tolerans', 'min tolerance')}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {tr('Çıkış Saati', 'End Time')}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="error.main">
                          {vardiya.endTime}
                          {vardiya.isNextDayExit && (
                            <Typography component="span" variant="caption" color="primary" sx={{ ml: 0.5 }}>
                              (+1)
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          -{vardiya.earlyExitTolerance || 10} {tr('dk tolerans', 'min tolerance')}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
                
                {/* Alt Kısım - Butonlar */}
                <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonIcon />}
                    onClick={() => handleOpenPersonnelModal(vardiya.id)}
                    sx={{ flex: 1 }}
                  >
                    {tr('Personel Ata', 'Assign Personnel')}
                  </Button>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => handleDeleteClick(vardiya)}
                    disabled={personnelCount > 0}
                    sx={{ 
                      border: '1px solid', 
                      borderColor: 'error.main',
                      opacity: personnelCount > 0 ? 0.5 : 1,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </VardiyaCard>
            );
          })}
        </Box>
      ) : (
        /* Desktop Tablo Görünümü */
        <TableContainer component={Paper} sx={{ 
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          borderRadius: 2,
          maxHeight: '65vh'
        }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="22%">{tr('Vardiya Adı', 'Shift Name')}</TableCell>
                <TableCell width="13%">{tr('QR Yöntemi', 'QR Method')}</TableCell>
                <TableCell width="9%">{tr('Giriş Saati', 'Start Time')}</TableCell>
                <TableCell width="9%">{tr('Çıkış Saati', 'End Time')}</TableCell>
                <TableCell width="13%">{tr('Geç Kalma Toleransı', 'Late Tolerance')}</TableCell>
                <TableCell width="13%">{tr('Erken Çıkma Toleransı', 'Early Exit Tolerance')}</TableCell>
                <TableCell width="12%">{tr('Personel Sayısı', 'Personnel Count')}</TableCell>
                <TableCell width="9%" align="center">{tr('İşlemler', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vardiyalar.map((vardiya) => {
                // Personel sayısını hesapla
                const personnelCount = vardiya.personnel ? 
                  (Array.isArray(vardiya.personnel) ? vardiya.personnel.length : Object.keys(vardiya.personnel).length) : 0;
                
                // Şube adını bul
                const branch = branches.find(branch => branch.id === vardiya.branchesId);
                const branchName = branch ? branch.name : '';
                
                return (
                  <TableRow key={vardiya.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => handleOpenPersonnelModal(vardiya.id)}
                          sx={{ 
                            mr: 1.5,
                            border: '1px solid', 
                            borderColor: 'primary.main',
                            borderRadius: '50%',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            }
                          }}
                        >
                          <PersonIcon fontSize="small" />
                          <AddIcon fontSize="small" sx={{ position: 'absolute', right: 0, bottom: 0, fontSize: 12, backgroundColor: 'white', borderRadius: '50%' }} />
                        </IconButton>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {vardiya.name}
                          </Typography>
                          {branchName && (
                            <Typography variant="caption" color="text.secondary">
                              {branchName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<QrCode2Icon sx={{ fontSize: 15 }} />}
                        label={vardiya.attendanceMode === 'dual' ? tr('Çift QR', 'Dual QR') : tr('Tek QR', 'Single QR')}
                        size="small"
                        variant={vardiya.attendanceMode === 'dual' ? 'filled' : 'outlined'}
                        color={vardiya.attendanceMode === 'dual' ? 'secondary' : 'default'}
                        sx={{ fontSize: '0.72rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {vardiya.startTime}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {vardiya.endTime}
                        </Typography>
                        {vardiya.isNextDayExit && (
                          <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                            ({tr('Ertesi gün', 'Next day')})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {vardiya.lateTolerance || 10} {tr('dakika', 'minutes')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {vardiya.earlyExitTolerance || 10} {tr('dakika', 'minutes')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${personnelCount} ${tr('kişi', 'people')}`}
                        size="small"
                        color={personnelCount > 0 ? "primary" : "default"}
                        variant={personnelCount > 0 ? "filled" : "outlined"}
                        sx={{ fontSize: '11px', height: 24 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={personnelCount > 0 ? 
                        tr('Personel atanmış vardiyalar silinemez', 'Shifts with assigned personnel cannot be deleted') : tr('Vardiyayı sil', 'Delete shift')}>
                        <span>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteClick(vardiya)}
                            sx={{ 
                              border: '1px solid', 
                              borderColor: 'error.main',
                              borderRadius: '50%',
                              padding: '4px',
                              opacity: personnelCount > 0 ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: personnelCount > 0 ? 'inherit' : 'error.main',
                                color: personnelCount > 0 ? 'error.main' : 'white',
                              }
                            }}
                            disabled={personnelCount > 0}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Vardiya Ekleme Modal */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6">
            {isManager && selectedBranch ? 
              `${selectedBranch.name} ${tr('Şubesine Vardiya Ekle', 'Branch - Add Shift')}` : 
              tr('Yeni Vardiya Ekle', 'Add New Shift')}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">{error}</Typography>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                autoFocus
                label={tr('Vardiya Adı', 'Shift Name')}
                placeholder={tr('Örn: Sabah Vardiyası, Gece Vardiyası', 'Ex: Morning Shift, Night Shift')}
                value={vardiyaAdi}
                onChange={(e) => setVardiyaAdi(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            
            {/* Şube seçimi - manager için disabled, ana admin için aktif */}
            <Grid item xs={12}>
              <Autocomplete
                id="branch-select"
                options={branches}
                getOptionLabel={(option) => option.name}
                value={selectedBranch}
                onChange={(event, newValue) => {
                  setSelectedBranch(newValue);
                }}
                loading={loadingBranches}
                disabled={isManager}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tr('Şube', 'Branch')}
                    placeholder={isManager ? tr('Kendi şubeniz', 'Your branch') : tr('Bir şube seçin veya boş bırakın', 'Select a branch or leave empty')}
                    helperText={isManager 
                      ? tr('Kendi şubenize vardiya ekliyorsunuz', 'You are adding a shift to your own branch') 
                      : tr('Vardiyayı belirli bir şubeyle ilişkilendirmek isterseniz seçebilirsiniz', 'You can choose a branch if you want to link this shift to one')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={tr('Şube bulunamadı', 'No branch found')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label={tr('Giriş Zamanı', 'Start Time')}
                type="time"
                value={girisZamani}
                onChange={(e) => setGirisZamani(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label={tr('Çıkış Zamanı', 'End Time')}
                type="time"
                value={cikisZamani}
                onChange={(e) => setCikisZamani(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cikisErtesiGun}
                    onChange={(e) => setCikisErtesiGun(e.target.checked)}
                    color="primary"
                  />
                }
                label={tr('Çıkış zamanı ertesi gün (Gece vardiyası)', 'End time is on next day (Night shift)')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{tr('Giriş/Çıkış Yöntemi', 'Check-in/out Method')}</InputLabel>
                <Select
                  value={attendanceMode}
                  onChange={(e) => setAttendanceMode(e.target.value as 'single' | 'dual')}
                  label={tr('Giriş/Çıkış Yöntemi', 'Check-in/out Method')}
                >
                  <MenuItem value="single">
                    {tr('Tek QR (otomatik giriş/çıkış)', 'Single QR (automatic in/out)')}
                  </MenuItem>
                  <MenuItem value="dual">
                    {tr('Çift QR (ayrı Giriş/Çıkış — çoklu oturum)', 'Dual QR (separate In/Out — multi-session)')}
                  </MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {attendanceMode === 'dual'
                  ? tr('Personel Giriş ve Çıkış QR kodlarını ayrı okutur; gün içinde birden fazla oturum desteklenir.',
                       'Personnel scan separate In and Out QR codes; multiple sessions per day are supported.')
                  : tr('Tek QR okutulur; giriş/çıkış saat penceresine göre otomatik belirlenir.',
                       'A single QR is scanned; in/out is determined automatically by the time window.')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{tr('Geç Kalma Toleransı (dk)', 'Late Tolerance (min)')}</InputLabel>
                <Select
                  value={gecKalmaToleransi}
                  onChange={(e) => setGecKalmaToleransi(Number(e.target.value))}
                  label={tr('Geç Kalma Toleransı (dk)', 'Late Tolerance (min)')}
                >
                  {[5, 10, 15, 20, 30, 45, 60].map((val) => (
                    <MenuItem key={val} value={val}>{val} {tr('dakika', 'minutes')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{tr('Erken Çıkma Toleransı (dk)', 'Early Exit Tolerance (min)')}</InputLabel>
                <Select
                  value={erkenCikmaToleransi}
                  onChange={(e) => setErkenCikmaToleransi(Number(e.target.value))}
                  label={tr('Erken Çıkma Toleransı (dk)', 'Early Exit Tolerance (min)')}
                >
                  {[5, 10, 15, 20, 30, 45, 60].map((val) => (
                    <MenuItem key={val} value={val}>{val} {tr('dakika', 'minutes')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? tr('Kaydediliyor...', 'Saving...') : tr('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Personel Seçme Modal */}
      <Dialog
        open={personnelModalOpen}
        onClose={handleClosePersonnelModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            height: '70vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, py: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{tr('Vardiyaya Personel Ekle', 'Add Personnel to Shift')}</Typography>
          <IconButton edge="end" color="inherit" onClick={handleClosePersonnelModal} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Autocomplete
            fullWidth
            size="small"
            options={personnelList}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={tr('Personel Ara', 'Search Personnel')}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
              />
            )}
            onChange={(_, newValue) => {
              if (newValue) {
                setSearchTerm(newValue.name);
              }
            }}
            noOptionsText={tr('Personel bulunamadı', 'No personnel found')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
            {tr('Not: Zaten başka vardiyaya atanmış personeller burada listelenmez. Bir personel sadece bir vardiyada olabilir.', 'Note: Personnel assigned to another shift are not listed here. One person can only be assigned to one shift.')}
          </Typography>
        </Box>
        
        <DialogContent dividers sx={{ p: 0, flexGrow: 1, overflow: 'auto' }}>
          {loadingPersonnel ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : filteredPersonnel.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {tr('Personel bulunamadı veya arama sonucu boş.', 'No personnel found or no search results.')}
              </Typography>
            </Box>
          ) : (
            <>
              <ListItem 
                button 
                onClick={handleToggleAll}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'action.hover',
                  py: 0.5,
                  minHeight: 40
                }}
                dense
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={selectedPersonnel.length === filteredPersonnel.length && filteredPersonnel.length > 0}
                    indeterminate={selectedPersonnel.length > 0 && selectedPersonnel.length < filteredPersonnel.length}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={tr('Tümünü Seç / Seçimi Kaldır', 'Select All / Clear Selection')} 
                  primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                />
              </ListItem>
              
              <List disablePadding>
                {filteredPersonnel.map((person) => {
                  const isSelected = selectedPersonnel.indexOf(person.id) !== -1;
                  
                  return (
                    <ListItem 
                      key={person.id} 
                      button 
                      onClick={() => handleTogglePersonnel(person.id)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: isSelected ? 'action.selected' : 'inherit',
                        py: 0.5,
                        minHeight: 36
                      }}
                      dense
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={person.name} 
                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>
        
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedPersonnel.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: 'center', fontSize: '0.85rem' }}>
                {tr('Seçilen:', 'Selected:')} {selectedPersonnel.length} {tr('personel', 'personnel')}
              </Typography>
            )}
            {selectedPersonnel.slice(0, 5).map((id) => {
              const person = personnelList.find(p => p.id === id);
              return person ? (
                <Chip 
                  key={id} 
                  label={person.name} 
                  size="small" 
                  onDelete={() => handleTogglePersonnel(id)}
                  sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.75rem' } }}
                />
              ) : null;
            })}
            {selectedPersonnel.length > 5 && (
              <Chip 
                label={`+${selectedPersonnel.length - 5} ${tr('kişi daha', 'more people')}`} 
                size="small" 
                variant="outlined"
                sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.75rem' } }}
              />
            )}
          </Box>
        </Box>
        
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={handleClosePersonnelModal} disabled={isSubmitting} size="small">
            {tr('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleSavePersonnel} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
            size="small"
          >
            {isSubmitting ? tr('Kaydediliyor...', 'Saving...') : tr('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Hata Modalı */}
      <Dialog
        open={deleteErrorOpen}
        onClose={handleCloseDeleteError}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{tr('Vardiya Silinemez', 'Shift Cannot Be Deleted')}</Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseDeleteError} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {tr('Personel atanmış vardiyalar silinemez. Lütfen önce vardiyadan tüm personeli kaldırınız.', 'Shifts with assigned personnel cannot be deleted. Please remove all personnel from the shift first.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteError} variant="contained" color="primary">
            {tr('Tamam', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{tr('Vardiya Silme Onayı', 'Shift Deletion Confirmation')}</Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseDeleteConfirm} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {tr(
              `"${deleteVardiyaName}" vardiyasını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
              `Are you sure you want to delete "${deleteVardiyaName}" shift? This action cannot be undone.`
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={isSubmitting}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isSubmitting ? tr('Siliniyor...', 'Deleting...') : tr('Evet, Sil', 'Yes, Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bildirim Snackbar */}
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VardiyaListesi;
